/**
 * Logic-limited, sandboxed template engine for the Secure Theme Engine.
 *
 * Goals / non-goals:
 *   - NO `eval`, no `Function`, no access to host globals — this file is a
 *     pure interpreter over a tiny grammar. The only inputs are a template
 *     string and a plain data context; the only output is a string.
 *   - NO network, NO filesystem — there is simply no API surface for it.
 *   - Bounded CPU/output: hard caps on input size, total interpreted steps,
 *     loop iterations, nesting depth, and output length. Exceeding any cap
 *     throws `TemplateError`; callers MUST catch and degrade gracefully.
 *   - Interpolations are HTML-escaped by default. There is intentionally NO
 *     "raw/unescaped" output directive.
 *
 * Grammar (Mustache-ish):
 *   text
 *   {{ expr }}                         -> escaped output
 *   {{ expr | filter }}               -> filter applied, then escaped
 *   {{ expr | filter:arg }}
 *   {{# if expr }} ... {{ else }} ... {{/ if }}
 *   {{# each expr as item }} ... {{/ each }}   (exposes @index/@first/@last)
 *
 * expr := <operand> [ <op> <operand> ]
 * operand := dotted.path | 'string' | "string" | number | true | false | null
 * op := == != > < >= <=
 * a leading `!` negates an operand/path in conditions.
 */

export class TemplateError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TemplateError";
  }
}

export interface TemplateLimits {
  maxInput: number;
  maxOutput: number;
  maxSteps: number;
  maxLoopIterations: number;
  maxDepth: number;
}

export const DEFAULT_LIMITS: TemplateLimits = {
  maxInput: 32 * 1024,
  maxOutput: 64 * 1024,
  maxSteps: 100_000,
  maxLoopIterations: 5_000,
  maxDepth: 32,
};

// ---------------------------------------------------------------------------
// Lexer / Parser
// ---------------------------------------------------------------------------

type TextNode = { t: "text"; v: string };
type OutNode = { t: "out"; expr: string };
type IfNode = { t: "if"; cond: string; then: Node[]; else: Node[] };
type EachNode = { t: "each"; expr: string; alias: string; body: Node[] };
type Node = TextNode | OutNode | IfNode | EachNode;

type Tag =
  | { kind: "text"; v: string }
  | { kind: "out"; v: string }
  | { kind: "if"; v: string }
  | { kind: "else" }
  | { kind: "endif" }
  | { kind: "each"; v: string }
  | { kind: "endeach" };

function lex(src: string): Tag[] {
  const tags: Tag[] = [];
  let i = 0;
  while (i < src.length) {
    const open = src.indexOf("{{", i);
    if (open === -1) {
      tags.push({ kind: "text", v: src.slice(i) });
      break;
    }
    if (open > i) tags.push({ kind: "text", v: src.slice(i, open) });
    const close = src.indexOf("}}", open + 2);
    if (close === -1) {
      throw new TemplateError("unterminated {{ ... }} tag");
    }
    const inner = src.slice(open + 2, close).trim();
    i = close + 2;
    if (inner.startsWith("#")) {
      const body = inner.slice(1).trim();
      if (/^if\b/.test(body)) tags.push({ kind: "if", v: body.replace(/^if\b/, "").trim() });
      else if (/^each\b/.test(body)) tags.push({ kind: "each", v: body.replace(/^each\b/, "").trim() });
      else throw new TemplateError(`unknown block: ${body}`);
    } else if (/^\/\s*if$/.test(inner)) {
      tags.push({ kind: "endif" });
    } else if (/^\/\s*each$/.test(inner)) {
      tags.push({ kind: "endeach" });
    } else if (inner === "else") {
      tags.push({ kind: "else" });
    } else if (inner.startsWith("/")) {
      throw new TemplateError(`unknown closing tag: ${inner}`);
    } else {
      tags.push({ kind: "out", v: inner });
    }
  }
  return tags;
}

function parse(tags: Tag[]): Node[] {
  let pos = 0;

  function parseList(stopKinds: Tag["kind"][]): { nodes: Node[]; stoppedAt: Tag | null } {
    const nodes: Node[] = [];
    while (pos < tags.length) {
      const tag = tags[pos];
      if (stopKinds.includes(tag.kind)) {
        return { nodes, stoppedAt: tag };
      }
      pos++;
      switch (tag.kind) {
        case "text":
          nodes.push({ t: "text", v: tag.v });
          break;
        case "out":
          nodes.push({ t: "out", expr: tag.v });
          break;
        case "if": {
          const thenPart = parseList(["else", "endif"]);
          let elsePart: Node[] = [];
          if (thenPart.stoppedAt?.kind === "else") {
            pos++; // consume else
            const e = parseList(["endif"]);
            elsePart = e.nodes;
            if (e.stoppedAt?.kind !== "endif") throw new TemplateError("missing {{/if}}");
            pos++; // consume endif
          } else if (thenPart.stoppedAt?.kind === "endif") {
            pos++; // consume endif
          } else {
            throw new TemplateError("missing {{/if}}");
          }
          nodes.push({ t: "if", cond: tag.v, then: thenPart.nodes, else: elsePart });
          break;
        }
        case "each": {
          const m = /^(.+?)\s+as\s+([a-zA-Z][a-zA-Z0-9_]*)\s*$/.exec(tag.v);
          if (!m) throw new TemplateError(`each must be "<expr> as <name>": ${tag.v}`);
          const body = parseList(["endeach"]);
          if (body.stoppedAt?.kind !== "endeach") throw new TemplateError("missing {{/each}}");
          pos++; // consume endeach
          nodes.push({ t: "each", expr: m[1].trim(), alias: m[2], body: body.nodes });
          break;
        }
        default:
          throw new TemplateError(`unexpected ${tag.kind}`);
      }
    }
    return { nodes, stoppedAt: null };
  }

  const { nodes, stoppedAt } = parseList([]);
  if (stoppedAt) throw new TemplateError(`unexpected ${stoppedAt.kind}`);
  return nodes;
}

// ---------------------------------------------------------------------------
// Expression evaluation
// ---------------------------------------------------------------------------

type Scope = Record<string, unknown>;

const SAFE_KEY = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;
const PROTO_KEYS = new Set(["__proto__", "prototype", "constructor"]);

function resolvePath(path: string, scopes: Scope[]): unknown {
  const parts = path.split(".");
  // Find the first scope (innermost last) that has the head key.
  const head = parts[0];
  if (!SAFE_KEY.test(head) || PROTO_KEYS.has(head)) return undefined;
  let current: unknown;
  let found = false;
  for (let s = scopes.length - 1; s >= 0; s--) {
    if (scopes[s] != null && Object.prototype.hasOwnProperty.call(scopes[s], head)) {
      current = (scopes[s] as Record<string, unknown>)[head];
      found = true;
      break;
    }
  }
  if (!found) return undefined;
  for (let p = 1; p < parts.length; p++) {
    const key = parts[p];
    if (!SAFE_KEY.test(key) || PROTO_KEYS.has(key)) return undefined;
    if (current == null) return undefined;
    if (typeof current !== "object") return undefined;
    if (!Object.prototype.hasOwnProperty.call(current, key)) {
      current = undefined;
      continue;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

const STRING_LIT = /^'([^']*)'$|^"([^"]*)"$/;
const NUMBER_LIT = /^-?\d+(\.\d+)?$/;

function evalOperand(token: string, scopes: Scope[]): unknown {
  const t = token.trim();
  if (t === "true") return true;
  if (t === "false") return false;
  if (t === "null") return null;
  const sm = STRING_LIT.exec(t);
  if (sm) return sm[1] !== undefined ? sm[1] : sm[2];
  if (NUMBER_LIT.test(t)) return Number(t);
  return resolvePath(t, scopes);
}

const COMPARATORS = ["==", "!=", ">=", "<=", ">", "<"] as const;

function evalCondition(expr: string, scopes: Scope[]): boolean {
  const trimmed = expr.trim();
  for (const op of COMPARATORS) {
    const idx = trimmed.indexOf(op);
    if (idx > 0) {
      const left = evalOperand(trimmed.slice(0, idx), scopes);
      const right = evalOperand(trimmed.slice(idx + op.length), scopes);
      switch (op) {
        case "==":
          return left === right;
        case "!=":
          return left !== right;
        case ">=":
          return Number(left) >= Number(right);
        case "<=":
          return Number(left) <= Number(right);
        case ">":
          return Number(left) > Number(right);
        case "<":
          return Number(left) < Number(right);
      }
    }
  }
  // bare truthiness, with optional leading "!"
  if (trimmed.startsWith("!")) {
    return !truthy(evalOperand(trimmed.slice(1), scopes));
  }
  return truthy(evalOperand(trimmed, scopes));
}

function truthy(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
}

// ---------------------------------------------------------------------------
// Filters (safe, pure, finite)
// ---------------------------------------------------------------------------

type Filter = (value: unknown, arg?: string) => unknown;

const FILTERS: Record<string, Filter> = {
  upper: (v) => String(v ?? "").toUpperCase(),
  lower: (v) => String(v ?? "").toLowerCase(),
  trim: (v) => String(v ?? "").trim(),
  default: (v, arg) => (v == null || v === "" ? (arg ?? "") : v),
  truncate: (v, arg) => {
    const n = Math.max(0, Math.min(2000, parseInt(arg ?? "80", 10) || 80));
    const s = String(v ?? "");
    return s.length > n ? s.slice(0, n) + "…" : s;
  },
  money: (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v ?? "");
    return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  },
};

function applyFilter(value: unknown, name: string, arg?: string): unknown {
  const f = FILTERS[name];
  if (!f) throw new TemplateError(`unknown filter: ${name}`);
  return f(value, arg);
}

function evalOutput(expr: string, scopes: Scope[]): string {
  // value | filter | filter:arg
  const segments = splitTopLevel(expr, "|");
  let value = evalOperand(segments[0], scopes);
  for (let i = 1; i < segments.length; i++) {
    const seg = segments[i].trim();
    const colon = seg.indexOf(":");
    if (colon === -1) {
      value = applyFilter(value, seg);
    } else {
      const fname = seg.slice(0, colon).trim();
      let arg = seg.slice(colon + 1).trim();
      const sm = STRING_LIT.exec(arg);
      if (sm) arg = sm[1] !== undefined ? sm[1] : sm[2];
      value = applyFilter(value, fname, arg);
    }
  }
  if (value == null) return "";
  return String(value);
}

/** Split on a separator that is not inside quotes. */
function splitTopLevel(input: string, sep: string): string[] {
  const out: string[] = [];
  let buf = "";
  let quote: string | null = null;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (quote) {
      if (ch === quote) quote = null;
      buf += ch;
    } else if (ch === "'" || ch === '"') {
      quote = ch;
      buf += ch;
    } else if (ch === sep) {
      out.push(buf);
      buf = "";
    } else {
      buf += ch;
    }
  }
  out.push(buf);
  return out;
}

// ---------------------------------------------------------------------------
// HTML escaping
// ---------------------------------------------------------------------------

const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
  "`": "&#96;",
};

export function escapeHtml(input: string): string {
  return input.replace(/[&<>"'`]/g, (c) => ESCAPE_MAP[c]);
}

// ---------------------------------------------------------------------------
// Renderer (with hard caps)
// ---------------------------------------------------------------------------

interface RenderState {
  out: string[];
  outLen: number;
  steps: number;
  loops: number;
  limits: TemplateLimits;
}

function emit(state: RenderState, s: string): void {
  state.outLen += s.length;
  if (state.outLen > state.limits.maxOutput) {
    throw new TemplateError("output limit exceeded");
  }
  state.out.push(s);
}

function tick(state: RenderState): void {
  state.steps++;
  if (state.steps > state.limits.maxSteps) {
    throw new TemplateError("step/CPU budget exceeded");
  }
}

function renderNodes(nodes: Node[], scopes: Scope[], state: RenderState, depth: number): void {
  if (depth > state.limits.maxDepth) {
    throw new TemplateError("max nesting depth exceeded");
  }
  for (const node of nodes) {
    tick(state);
    switch (node.t) {
      case "text":
        emit(state, node.v);
        break;
      case "out":
        emit(state, escapeHtml(evalOutput(node.expr, scopes)));
        break;
      case "if":
        if (evalCondition(node.cond, scopes)) {
          renderNodes(node.then, scopes, state, depth + 1);
        } else {
          renderNodes(node.else, scopes, state, depth + 1);
        }
        break;
      case "each": {
        const list = evalOperand(node.expr, scopes);
        if (!Array.isArray(list)) break;
        const len = list.length;
        for (let idx = 0; idx < len; idx++) {
          state.loops++;
          if (state.loops > state.limits.maxLoopIterations) {
            throw new TemplateError("loop iteration budget exceeded");
          }
          tick(state);
          const childScope: Scope = {
            [node.alias]: list[idx],
            "@index": idx,
            "@first": idx === 0,
            "@last": idx === len - 1,
          };
          renderNodes(node.body, [...scopes, childScope], state, depth + 1);
        }
        break;
      }
    }
  }
}

export interface CompiledTemplate {
  render(context: Record<string, unknown>): string;
}

/**
 * Compile a template once into a reusable, bounded renderer. Parsing happens
 * here; rendering is cheap and re-runnable with different data contexts.
 * Throws `TemplateError` on malformed templates or oversized input.
 */
export function compileTemplate(
  source: string,
  limits: TemplateLimits = DEFAULT_LIMITS,
): CompiledTemplate {
  if (typeof source !== "string") {
    throw new TemplateError("template source must be a string");
  }
  if (source.length > limits.maxInput) {
    throw new TemplateError("template input too large");
  }
  const ast = parse(lex(source));
  return {
    render(context: Record<string, unknown>): string {
      const state: RenderState = { out: [], outLen: 0, steps: 0, loops: 0, limits };
      renderNodes(ast, [context ?? {}], state, 0);
      return state.out.join("");
    },
  };
}

/** One-shot convenience: compile + render. */
export function renderTemplate(
  source: string,
  context: Record<string, unknown>,
  limits: TemplateLimits = DEFAULT_LIMITS,
): string {
  return compileTemplate(source, limits).render(context);
}
